import React, { useState, useEffect } from 'react';
import UserIcon from './icons/UserIcon';
import { toChefImageSrc } from '../utils/chefImage';

export type ChefAvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'hero';

interface ChefAvatarProps {
    imageUrl?: string;
    name?: string;
    size?: ChefAvatarSize;
    className?: string;
    variant?: 'thumb' | 'large';
}

const SIZE_CLASSES: Record<ChefAvatarSize, { container: string; icon: string }> = {
    xs: { container: 'w-10 h-10 rounded-xl shrink-0', icon: 'w-5 h-5' },
    sm: { container: 'w-8 h-8 rounded-full shrink-0', icon: 'w-4 h-4' },
    md: { container: 'w-14 h-14 rounded-2xl shrink-0', icon: 'w-7 h-7' },
    lg: { container: 'w-full h-40 rounded-2xl', icon: 'w-12 h-12' },
    xl: { container: 'w-28 h-28 rounded-full shrink-0', icon: 'w-10 h-10' },
    hero: { container: 'w-full h-48', icon: 'w-16 h-16' },
};

const ChefAvatar: React.FC<ChefAvatarProps> = ({
    imageUrl,
    name,
    size = 'sm',
    className = '',
    variant = 'large',
}) => {
    const [failed, setFailed] = useState(false);
    const resolvedUrl = toChefImageSrc(imageUrl);
    const { container, icon } = SIZE_CLASSES[size];

    useEffect(() => {
        setFailed(false);
    }, [imageUrl]);

    if (resolvedUrl && !failed) {
        return (
            <img
                src={resolvedUrl}
                alt={name || ''}
                referrerPolicy="no-referrer"
                loading="lazy"
                className={`object-cover border border-slate-200 dark:border-slate-700 ${container} ${className}`}
                onError={() => setFailed(true)}
            />
        );
    }

    const isWide = size === 'lg' || size === 'hero';
    return (
        <div
            className={`flex items-center justify-center bg-gradient-to-br from-brand-sage/20 to-brand-orange/10 dark:from-brand-sage/30 dark:to-slate-800 border border-slate-200 dark:border-slate-700 ${container} ${className}`}
        >
            <div className={`rounded-full bg-white/80 dark:bg-slate-800/80 shadow-lg text-brand-sage flex items-center justify-center ${isWide ? 'p-6' : 'p-2'}`}>
                <UserIcon className={icon} />
            </div>
        </div>
    );
};

export default ChefAvatar;
